import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Key, Plus, Copy, Trash2, Eye, EyeOff, AlertTriangle, CheckCircle2, Loader2 } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { useAuthStore } from '@/stores/authStore';
import { useSubscription } from '@/hooks/useSubscription';

interface APIKey {
  id: string;
  name: string;
  key_prefix: string;
  permissions: string[];
  rate_limit_per_hour: number;
  rate_limit_per_day: number;
  last_used_at: string | null;
  created_at: string;
  is_active: boolean;
}

export function ApiKeysSection() {
  const { user } = useAuthStore();
  const { limits } = useSubscription();
  const [apiKeys, setApiKeys] = useState<APIKey[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isCreating, setIsCreating] = useState(false);
  const [showCreateDialog, setShowCreateDialog] = useState(false);
  const [newKeyName, setNewKeyName] = useState('');
  const [newKeyPermissions, setNewKeyPermissions] = useState<string[]>(['read']);
  const [createdKey, setCreatedKey] = useState<{ key: string; secret: string } | null>(null);
  const [showSecrets, setShowSecrets] = useState<Record<string, boolean>>({});

  useEffect(() => {
    loadApiKeys();
  }, []);

  const loadApiKeys = async () => {
    try {
      const { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setApiKeys(data || []);
    } catch (error) {
      console.error('Failed to load API keys:', error);
      toast.error('Failed to load API keys');
    } finally {
      setIsLoading(false);
    }
  };

  const generateApiKey = () => {
    const array = new Uint8Array(32);
    crypto.getRandomValues(array);
    return 'nxqr_' + Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const generateApiSecret = () => {
    const array = new Uint8Array(48);
    crypto.getRandomValues(array);
    return 'nxqr_secret_' + Array.from(array, byte => byte.toString(16).padStart(2, '0')).join('');
  };

  const createApiKey = async () => {
    if (!newKeyName.trim()) {
      toast.error('Please enter a key name');
      return;
    }

    setIsCreating(true);
    try {
      const apiKey = generateApiKey();
      const apiSecret = generateApiSecret();
      const keyPrefix = apiKey.substring(0, 12) + '...';

      // Get rate limits based on subscription
      const { data: rateLimits } = await supabase.rpc('get_api_rate_limits', {
        p_user_id: user?.id
      });

      const hourlyLimit = rateLimits?.[0]?.hourly_limit || 50;
      const dailyLimit = rateLimits?.[0]?.daily_limit || 500;

      const { error } = await supabase
        .from('api_keys')
        .insert({
          user_id: user?.id!,
          name: newKeyName,
          key_hash: apiKey,
          key_prefix: keyPrefix,
          permissions: newKeyPermissions,
          rate_limit_per_hour: hourlyLimit,
          rate_limit_per_day: dailyLimit
        });

      if (error) throw error;

      setCreatedKey({ key: apiKey, secret: apiSecret });
      await loadApiKeys();
      toast.success('API key created successfully');
    } catch (error) {
      console.error('Failed to create API key:', error);
      toast.error('Failed to create API key');
    } finally {
      setIsCreating(false);
    }
  };

  const deleteApiKey = async (id: string, name: string) => {
    if (!confirm(`Are you sure you want to delete the API key "${name}"? This action cannot be undone.`)) {
      return;
    }

    try {
      const { error } = await supabase
        .from('api_keys')
        .delete()
        .eq('id', id);

      if (error) throw error;

      await loadApiKeys();
      toast.success('API key deleted successfully');
    } catch (error) {
      console.error('Failed to delete API key:', error);
      toast.error('Failed to delete API key');
    }
  };

  const copyToClipboard = async (text: string, label: string) => {
    await navigator.clipboard.writeText(text);
    toast.success(`${label} copied to clipboard`);
  };

  const togglePermission = (permission: string) => {
    setNewKeyPermissions(prev =>
      prev.includes(permission)
        ? prev.filter(p => p !== permission)
        : [...prev, permission]
    );
  };

  const closeCreateDialog = () => {
    setShowCreateDialog(false);
    setNewKeyName('');
    setNewKeyPermissions(['read']);
    setCreatedKey(null);
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="pt-6">
          <div className="flex items-center justify-center py-8">
            <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="space-y-6"
      >
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" />
              API Keys
            </CardTitle>
            <CardDescription>
              Manage your REST API access keys. Use these to integrate NexQR with your applications.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Your API Keys ({apiKeys.length})</p>
                <p className="text-xs text-muted-foreground">
                  Rate Limit: Based on your plan
                </p>
              </div>
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Create API Key
              </Button>
            </div>

            {apiKeys.length === 0 ? (
              <div className="text-center py-12 border-2 border-dashed rounded-lg">
                <Key className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <h3 className="font-medium text-lg mb-2">No API Keys Yet</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Create your first API key to start integrating with NexQR
                </p>
                <Button onClick={() => setShowCreateDialog(true)}>
                  <Plus className="h-4 w-4 mr-2" />
                  Create Your First API Key
                </Button>
              </div>
            ) : (
              <div className="space-y-3">
                {apiKeys.map((key) => (
                  <Card key={key.id} className="border">
                    <CardContent className="pt-6">
                      <div className="flex items-start justify-between">
                        <div className="space-y-2 flex-1">
                          <div className="flex items-center gap-2">
                            <h4 className="font-medium">{key.name}</h4>
                            <Badge variant={key.is_active ? 'default' : 'secondary'}>
                              {key.is_active ? 'Active' : 'Inactive'}
                            </Badge>
                          </div>
                          
                          <div className="flex items-center gap-2 text-sm text-muted-foreground font-mono">
                            <span>{key.key_prefix}</span>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-6 px-2"
                              onClick={() => copyToClipboard(key.key_prefix, 'Key prefix')}
                            >
                              <Copy className="h-3 w-3" />
                            </Button>
                          </div>

                          <div className="flex flex-wrap gap-2">
                            {key.permissions.map((perm) => (
                              <Badge key={perm} variant="outline" className="text-xs">
                                {perm}
                              </Badge>
                            ))}
                          </div>

                          <div className="flex gap-4 text-xs text-muted-foreground">
                            <span>Rate Limit: {key.rate_limit_per_hour}/hour, {key.rate_limit_per_day}/day</span>
                            {key.last_used_at && (
                              <span>Last used: {new Date(key.last_used_at).toLocaleDateString()}</span>
                            )}
                          </div>
                        </div>

                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => deleteApiKey(key.id, key.name)}
                          className="text-destructive hover:text-destructive hover:bg-destructive/10"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </CardContent>
                  </Card>
                ))}
              </div>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>API Documentation</CardTitle>
            <CardDescription>
              Learn how to integrate with NexQR API
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="bg-muted/50 rounded-lg p-4 space-y-3">
              <h4 className="font-medium">Available Endpoints:</h4>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono">POST</Badge>
                  <code className="text-xs">/api/v1/qr/create</code>
                  <span className="text-muted-foreground">- Create QR code</span>
                </li>
                <li className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono">GET</Badge>
                  <code className="text-xs">/api/v1/qr/:id</code>
                  <span className="text-muted-foreground">- Get QR details</span>
                </li>
                <li className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono">PATCH</Badge>
                  <code className="text-xs">/api/v1/qr/:id</code>
                  <span className="text-muted-foreground">- Update QR code</span>
                </li>
                <li className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono">DELETE</Badge>
                  <code className="text-xs">/api/v1/qr/:id</code>
                  <span className="text-muted-foreground">- Delete QR code</span>
                </li>
                <li className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono">GET</Badge>
                  <code className="text-xs">/api/v1/qr/:id/analytics</code>
                  <span className="text-muted-foreground">- Get analytics</span>
                </li>
                <li className="flex items-center gap-2">
                  <Badge variant="outline" className="font-mono">POST</Badge>
                  <code className="text-xs">/api/v1/batch</code>
                  <span className="text-muted-foreground">- Bulk create QR codes</span>
                </li>
              </ul>
            </div>

            <div className="bg-blue-50 dark:bg-blue-950 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
              <h4 className="font-medium text-blue-900 dark:text-blue-100 mb-2">Authentication</h4>
              <p className="text-sm text-blue-700 dark:text-blue-300">
                Include your API key in the <code className="bg-blue-100 dark:bg-blue-900 px-1 rounded">x-api-key</code> header with every request.
              </p>
              <pre className="mt-2 bg-blue-100 dark:bg-blue-900 p-2 rounded text-xs overflow-x-auto">
{`curl -X POST https://vliudbxhddvxvduyjdxl.supabase.co/functions/v1/api-v1-qr-create \\
  -H "x-api-key: your_api_key_here" \\
  -H "Content-Type: application/json" \\
  -d '{"type": "url", "content": "https://example.com", "title": "My QR"}'`}
              </pre>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Create API Key Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={closeCreateDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Create New API Key</DialogTitle>
            <DialogDescription>
              {createdKey ? 'Save your API key and secret - they won\'t be shown again!' : 'Generate a new API key for programmatic access'}
            </DialogDescription>
          </DialogHeader>

          {createdKey ? (
            <div className="space-y-4">
              <div className="bg-green-50 dark:bg-green-950 border border-green-200 dark:border-green-800 rounded-lg p-4">
                <div className="flex gap-2 items-start mb-3">
                  <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 flex-shrink-0" />
                  <div>
                    <h4 className="font-medium text-green-900 dark:text-green-100">API Key Created!</h4>
                    <p className="text-sm text-green-700 dark:text-green-300">
                      Copy these credentials now - you won't be able to see them again.
                    </p>
                  </div>
                </div>

                <div className="space-y-3">
                  <div>
                    <Label className="text-xs text-green-900 dark:text-green-100">API Key</Label>
                    <div className="flex items-center gap-2 mt-1">
                      <Input
                        value={createdKey.key}
                        readOnly
                        className="font-mono text-xs bg-green-100 dark:bg-green-900 border-green-300 dark:border-green-700"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => copyToClipboard(createdKey.key, 'API key')}
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          ) : (
            <div className="space-y-4">
              <div>
                <Label htmlFor="key-name">Key Name</Label>
                <Input
                  id="key-name"
                  placeholder="e.g., Production Server"
                  value={newKeyName}
                  onChange={(e) => setNewKeyName(e.target.value)}
                  className="mt-1"
                />
              </div>

              <div>
                <Label>Permissions</Label>
                <div className="space-y-2 mt-2">
                  {['read', 'write', 'delete', 'analytics'].map((permission) => (
                    <div key={permission} className="flex items-center space-x-2">
                      <Checkbox
                        id={permission}
                        checked={newKeyPermissions.includes(permission)}
                        onCheckedChange={() => togglePermission(permission)}
                      />
                      <label
                        htmlFor={permission}
                        className="text-sm font-medium leading-none peer-disabled:cursor-not-allowed peer-disabled:opacity-70 capitalize"
                      >
                        {permission}
                      </label>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )}

          <DialogFooter>
            {createdKey ? (
              <Button onClick={closeCreateDialog} className="w-full">
                Done
              </Button>
            ) : (
              <>
                <Button variant="outline" onClick={closeCreateDialog}>
                  Cancel
                </Button>
                <Button onClick={createApiKey} disabled={isCreating}>
                  {isCreating ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Creating...
                    </>
                  ) : (
                    <>
                      <Key className="h-4 w-4 mr-2" />
                      Create Key
                    </>
                  )}
                </Button>
              </>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
